import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { HttpStatusCode } from '../shared/constants/http-status-code.enum';
import { InstrumentationData } from '../shared/instrumentation/instrumentation-data.model';
import { InstrumentationParam, InstrumentationParams, KnownAreas, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FileItem } from '../shared/models/file-item.model';
import { FileUrlModel } from '../shared/models/file-url.model';
import { GraphError } from '../shared/models/graph.model';
import { KnownWorkloadReferralNames } from '../shared/models/known-workload-referral-names.model';
import { GraphDriveItem } from '../shared/models/onedrive-graph.model';
import { ResultsWithCacheObservableError } from '../shared/models/operation-stream.model';
import { OneDriveService } from '../shared/services/onedrive.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { WindowService } from '../shared/services/window.service';
import { XhrServiceError } from '../shared/services/xhr.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { GraphErrorResolver } from '../shared/utilities/graph-error-resolver.utility';
import { UrlBuilder } from '../shared/utilities/url-builder.utility';
import { ResumableUploadState, UploadError, UploadErrorType, UploadService, UploadSession, UploadSessionState, UploadSource } from './models/upload-session.model';
import { UploadConfigService } from './upload-config.service';
import { UploadStateMachine } from './upload-state-machine';

/**
 * Service that uploads documents to OneDrive.
 */
@Injectable({
    providedIn: 'root'
})
export class OneDriveUploadService implements UploadService<FileUrlModel> {

    public uploadEnabledPromise: Promise<boolean>;

    private readonly stateMachine: UploadStateMachine;

    constructor(
        private readonly contextService: ContextService,
        private readonly locService: LocService,
        private readonly oneDriveService: OneDriveService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly windowService: WindowService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) {
        this.setupUploadPromise();

        if (this.uploadConfigService.config) {
            this.stateMachine = new UploadStateMachine(this.oneDriveService, this.uploadConfigService, this.windowService);
        }
    }

    public upload(file: File, sessionId: string, uploadSource: UploadSource): Observable<UploadSession<FileUrlModel>> {
        const config = this.uploadConfigService.config;
        if (!config) {
            return null;
        }

        const uploadSession = new UploadSession<FileUrlModel>(sessionId, file, uploadSource);
        const uploadObservable = new BehaviorSubject<UploadSession<FileUrlModel>>(uploadSession);

        uploadSession.cancellationToken.onCancel(() => {
            if (uploadSession.sessionUrl && !uploadSession.finished()) {
                this.oneDriveService.cancelUploadSession(uploadSession.sessionUrl, uploadSession.sessionId);
            }

            uploadSession.state = UploadSessionState.Cancelled;
            uploadObservable.next(uploadSession);
        });

        uploadSession.markCompletedAndOpenedInOfficeHome = () => {
            uploadSession.state = UploadSessionState.CompletedAndOpened;
            uploadObservable.next(uploadSession);
        };

        if (!this.isValidFileType(file)) {
            uploadSession.error = this.suggestOneDriveErrorWithText(config.oneDriveResources.errorTitle,
                this.locService.format(config.oneDriveResources.unsupportedFile, this.locService.OpenOneDrive),
                UploadErrorType.InvalidFileType,
                uploadSession);

            uploadSession.state = UploadSessionState.Failed;
            uploadObservable.next(uploadSession);
        } else {
            this.startUpload(uploadSession, uploadObservable);
        }

        return uploadObservable.asObservable();
    }

    private setupUploadPromise(): Promise<boolean> {
        this.uploadEnabledPromise = new Promise(resolve => {
            if (this.capabilitiesService.capabilities.oneDriveUploadEnabled && this.uploadConfigService.config) {
                this.oneDriveService.getMyDriveRootUrl()
                .then(rootUrl => {
                    // Most upload errors manifest when the drive is innaccesible
                    resolve(!!rootUrl);
                })
                .catch((error: ResultsWithCacheObservableError) => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.IRW0y);

                    const response = error?.networkError?.httpResponse;
                    const isMsa = this.clientStartupConfigService.config.authType === AuthType.MSA;
                    const interactiveLoginRequired = response?.status === HttpStatusCode.TokenUnavailable && (isMsa || response?.data === 'CAP_Error');
                    if (!this.capabilitiesService.capabilities.isPwaInUwp &&
                        (isMsa || this.uploadConfigService.config.capPopupEnabled) &&
                        interactiveLoginRequired) {
                            resolve(true); // We might need to interactively fetch the token, so show the upload button even if token fetch failed.
                            return;
                    }

                    // TODO: Display a helpful error message instead of hiding the upload button
                    resolve(false);
                });
            } else {
                resolve(false);
            }
        });

        return this.uploadEnabledPromise;
    }

    private startUpload(uploadSession: UploadSession<FileUrlModel>, uploadObservable: BehaviorSubject<UploadSession<FileUrlModel>>): void {
        uploadSession.state = UploadSessionState.Started;
        uploadObservable.next(uploadSession);

        this.uploadToOneDrive(uploadSession).then(url => {
            if (uploadSession.file.type.indexOf('video') !== -1) {
                url.webUrl = UrlBuilder.appendQueryParam(url.webUrl, 'web', '1'); // Redirecting to odsp player
            }
            uploadSession.result = url;
            uploadSession.state = UploadSessionState.Completed;
            uploadObservable.next(uploadSession);
        }, (error: XhrServiceError<GraphDriveItem | GraphError>) => {
            uploadSession.error = this.getUploadSessionError(error, uploadSession, uploadObservable);
            uploadSession.state = UploadSessionState.Failed;
            uploadObservable.next(uploadSession);
        });
    }

    private isValidFileType(file: File): boolean {
        const fileType = FileItem.fileTypeFromFileName(file.name);
        return this.uploadConfigService.config.uploadFileTypes.has(fileType);
    }

    private uploadToOneDrive(uploadSession: UploadSession<FileUrlModel>): Promise<FileUrlModel> {
        // OneDrive API requires multi-stage upload for files over 4MB
        const upload = uploadSession.file.size < this.uploadConfigService.config.simpleUploadMaxSize
            ? this.uploadSmall(uploadSession)
            : this.uploadLarge(uploadSession);
        return upload.then(item => {
            const fileUrlModel = {} as FileUrlModel;
            if (item.webDavUrl) {
                fileUrlModel.canonicalUrl = item.webDavUrl;
            }

            let webUrl = item.webUrl;
            const type = FileItem.fileTypeFromFileName(uploadSession.file.name);
            if (this.uploadConfigService.config.editableFileTypes.has(type)) {
                // Launch editable files in edit mode
                const editQueryParam = 'action=edit';
                webUrl = webUrl.replace(new RegExp('([\?&])action[^&]*'), '$1' + editQueryParam);
                if (webUrl.lastIndexOf(editQueryParam) === -1) {
                    webUrl = UrlBuilder.appendQuery(webUrl, editQueryParam);
                }
            }
            fileUrlModel.webUrl = webUrl;
            return fileUrlModel;
        });
    }

    private uploadSmall(uploadSession: UploadSession<FileUrlModel>): Promise<GraphDriveItem> {
        return this.oneDriveService.uploadFile(
            uploadSession.file,
            uploadSession.sessionId,
            null,
            null,
            progress => uploadSession.updateProgress(progress.loaded),
            uploadSession.cancellationToken);
    }

    private uploadLarge(uploadSession: UploadSession<FileUrlModel>): Promise<GraphDriveItem> {
        return this.uploadState(uploadSession, {});
    }

    // This method was adapted from the OneDrive codebase: https://onedrive.visualstudio.com/OneDriveWeb/_git/odsp-next?path=%2Fsrc%2Flocal%2FdataSources%2Fupload%2Fvroom%2FVroomUploader.ts&version=GBmaster&_a=contents
    private uploadState(session: UploadSession<FileUrlModel>, state: ResumableUploadState): Promise<GraphDriveItem> {
        const {
            sessionUrl,
            item,
            error
        } = state;

        session.sessionUrl = sessionUrl;

        return new Promise<GraphDriveItem>((resolve, reject) => {
            if (item) {
                // Success state.
                resolve(item);
            } else if (error && !sessionUrl) {
                // The session was never created, so no need to destroy it
                reject(error);
            } else if (error) {
                // Destroy the started upload session
                this.oneDriveService.cancelUploadSession(sessionUrl, session.sessionId, session.cancellationToken)
                    .catch(() => { /* Ignore error. */ })
                    .then(() => reject(error));
            } else {
                this.stateMachine.advanceState(session, state, session.cancellationToken).then(
                    nextState => {
                        // Cycle back through the machine.
                        this.uploadState(session, nextState)
                            .then(resolve, reject);
                    },
                    reject);
            }
        });
    }

    private getUploadSessionError(error: XhrServiceError<GraphDriveItem | GraphError>, session: UploadSession<FileUrlModel>, uploadObservable: BehaviorSubject<UploadSession<FileUrlModel>>): UploadError {
        const oneDriveResources = this.uploadConfigService.config.oneDriveResources;
        if (!error.httpResponse || error.clientError) {
            return {
                title: oneDriveResources.errorTitle,
                type: UploadErrorType.Unknown,
                message: ''
            };
        }

        const commonResources = this.uploadConfigService.config.commonResources;
        const resolveError = (code: string | number | HttpStatusCode) => {
            switch (code) {
                case 'quotaLimitReached':
                case HttpStatusCode.InsufficientStorage:
                    return this.suggestOneDriveErrorWithText(oneDriveResources.errorTitle,
                        oneDriveResources.outOfSpace,
                        UploadErrorType.OutOfSpace,
                        session);
                case 'maxFileSizeExceeded':
                case HttpStatusCode.RequestEntityTooLarge:
                    return {
                        title: oneDriveResources.errorTitle,
                        type: UploadErrorType.FileTooLarge,
                        message: oneDriveResources.largeFile
                    };
                case HttpStatusCode.Offline:
                    return this.retryErrorWithText(oneDriveResources.errorTitle,
                        commonResources.checkConnection,
                        UploadErrorType.BadConnection,
                        session,
                        uploadObservable);
                case HttpStatusCode.TokenUnavailable:
                case HttpStatusCode.TokenRefreshFailed:
                    return { // TODO: Should we prompt for authentication (page refresh)?
                        title: oneDriveResources.errorTitle,
                        type: UploadErrorType.Auth,
                        message: ''
                    };
            }

            return undefined;
        };

        const resolution = GraphErrorResolver.resolveGraphError(error, resolveError);

        return resolution || {
            title: oneDriveResources.errorTitle,
            type: UploadErrorType.Unknown,
            message: ''
        };
    }

    private retryErrorWithText(errorTitle: string, text: string, errorType: UploadErrorType, session: UploadSession<FileUrlModel>, uploadObservable: BehaviorSubject<UploadSession<FileUrlModel>>): UploadError {
        const resources = this.uploadConfigService.config.commonResources;
        return {
            title: errorTitle,
            type: errorType,
            message: text,
            recourseAction: {
                text: resources.retryButtonTitle,
                ariaLabel: resources.retryButtonAriaLabel,
                action: () => {
                    this.startUpload(session, uploadObservable);
                },
                clickData: this.recourseClickDataWithId('upload-retry', session.sessionId, session.uploadSource)
            }
        };
    }

    private suggestOneDriveErrorWithText(errorTitle: string, text: string, errorType: UploadErrorType, session: UploadSession<FileUrlModel>): UploadError {
        if (this.contextService.sharedContext.oneDriveUrl) {
            return {
                title: errorTitle,
                type: errorType,
                message: text,
                recourseAction: {
                    text: this.locService.OpenOneDrive,
                    ariaLabel: this.locService.OpenOneDrive,
                    action: () => {
                        if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                            import('@office-start/launcher-service').then(
                                launchSvc => {
                                    launchSvc.launchUrl(this.contextService.sharedContext.oneDriveUrl, {action: 'OpenOneDriveUploadAction'});
                                }
                            );
                        } else {
                            this.urlLauncherService.openUrl(this.contextService.sharedContext.oneDriveUrl, LaunchAction.OpenOneDriveUploadAction);
                        }
                    },
                    clickData: this.recourseClickDataWithId('upload-open-onedrive', session.sessionId, session.uploadSource, KnownWorkloadReferralNames.OneDrive)
                }
            };
        } else {
            return {
                title: errorTitle,
                type: UploadErrorType.Unknown,
                message: ''
            };
        }
    }

    private recourseClickDataWithId(elementId: string, sessionId: string, uploadSource: UploadSource, referral?: string): InstrumentationData {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.UploadSessionId] = sessionId;
        params[InstrumentationParam.ElementId] = elementId;
        params[InstrumentationParam.Referral] = referral;
        params[InstrumentationParam.Area] = KnownAreas.UploadDialog;
        params[InstrumentationParam.ItemCategory] = UploadSource[uploadSource];

        return {
            eventName: KnownInstrumentationEvents.UploadClick,
            params,
            metrics: []
        };
    }
}
